import { EnumDiseaseType, EnumMedicineCategory, SymptomType } from 'src/constants/enum';
import { ORDER_PAYMENT, ORDER_STATUS, ORDER_CATEGORY } from 'src/constants/Order';
import { EnumInvoiceStatus } from 'src/services/OrderService';
import { ProcessMethod } from './DrugStoreEntity';
import { ILogistic } from './logistics';

/**
 * 订单详情接口
 * @export
 * @interface IOrderInfo
 */
export interface IOrderInfo {
  id: string;
  /** 脉景订单号 */
  out_trade_no: string;
  /** 支付平台订单号 */
  platform_trade_no: string;
  /** 第三方支付平台名称 */
  platform_name?: string;
  store: number;
  drug_store_name: string;
  /** 药库 */
  readonly medicine_groups: string;
  pay_status: number;
  /**
   * 折扣率
   */
  discount: number;
  /**
   * 待废弃，使用 order_payment_amount 代替
   */
  total_price: number;
  drug_total_floor_price: number;
  /**
   * 实付金额 (药品供货价+代煎费+运费) 后台计算
   */
  order_payment_amount: string;
  /** 药品总额 */
  original_total_price: number;
  /** 应收金额(订单不参与分账时使用) */
  transfer_amount_after_tax: string;
  user_profit_amount?: {
    /** old 应收金额(订单参与分账时使用)  */
    /** new 订单金额, 分账与不分账的逻辑后端处理 */
    user_receivable_profit_amount?: number;
    /** 实收金额(订单参与分账时使用) */
    user_received_profit_amount?: number;
    /** 分潤状态 */
    distribute_success: boolean;
    /** 分润退款金额， 没有时返回None */
    user_refund_profit_amount?: number;
  };

  currency: string;
  payment_id?: string;
  payment_type: ORDER_PAYMENT;
  doctor_uuid: string;
  doctor_name: string;
  doctor_email: string;
  doctor_mobile: string;
  sender_name: string;
  receiver_name: string;
  receiver_structure_address: string;
  receiver_address: string;
  receiver_mobile: string;
  patient_name: string;
  patient_gender: string;
  patient_mobile?: any;
  doctor_remarks: string;
  agent_remarks: string;
  items: IOrderItem[];
  prescriptions: IOrderPrescription[];
  /** 病历编号 */
  medical_record_code?: string;
  /** 运费 */
  shipping_cost?: number;
  /**
   * 类型
   */
  category: ORDER_CATEGORY;
  created_time: string;
  modified_time: string;

  /** 电子处方快照 */
  e_prescription_snapshot?: IPrescriptionSnapshot;

  /** 该订单对应发票的状态 */
  invoice_issue_status: 'NOT_YET_ISSUED' | 'HAVE_BEEN_ISSUED';
  /** 为前端封装的表达发票状态的字段 */
  invoice_issue_status_wrapper: EnumInvoiceStatus;
  /** 该订单是否申请过发票 */
  is_already_apply_for_invoice: boolean;
  /** ?? */
  decoction_amount: number;
  /** 草药代煎费用 */
  decoction_price: number;
  /** 是否使用草药代煎 */
  decoction_enabled: number;
  /** 物流信息 */
  express?: ILogistic;
  /** 首次打印商品清单时间 */
  first_print_prescription_time?: string;
  /** 下发时间 */
  distribute_time?: string;
  /** 首次打印物流运单时间 */
  first_print_logistics_time?: string;
  /** 是否打印处方客户联 */
  is_provide_prescription?: boolean;
  /** 加工方式 */
  processing_method: ProcessMethod;
  /**  是否开启加工服务 */
  processing_enabled: boolean;
  /** 必选代加工服务 */
  processing_required: boolean;
  /** 加工费用 */
  processing_price: number;
  /** 包装费用 */
  package_price: number;
  /** 机构名称 */
  institution_name: string;
  /** 是否已经发送药品订单  */
  // TODO: LELE
  send_medicine_order_status: boolean;
  send_loading?: boolean;
  /** 促销优惠金额 */
  promotion_discount_amount: string;
  /** 是否为外部创建 */
  is_created_by_external: boolean;
  /** 统筹金额 */
  overall_amount: number;
  /** 个账金额 */
  self_pay_amount: number;
  /** 自费 */
  personal_amount: number;
  /** 药品取药码 */
  medicine_take_code: string;
  /** 药品取药方式 自提 | 快递 */
  medicine_take_method: 'SELF' | 'EXPRESS';
  /** 是否使用医保 */
  is_use_medical_insurance: boolean | null;
  /** 医保金额 */
  medical_insurance_amount: number;
  /** 自提-取药详细地址 */
  self_mention_location: string;
  /** 自提-结构省市区地址 */
  self_mention_address: string;
  /** 是否存在退款订单 */
  is_exist_refund_order: boolean;
  /** 揽件开始时间 */
  delivery_start_time: string;
  /** 揽件结束时间 */
  delivery_end_time: string;
  /** 药品发货清单 */
  has_deliver_list: boolean;
  processing_methods: ProcessMethod[];
}

/** 订单列表用到的数据结果 */
export type IOrderListInfo = Pick<
  IOrderInfo,
  | 'id'
  | 'currency'
  | 'out_trade_no'
  | 'medicine_groups'
  | 'distribute_time'
  | 'invoice_issue_status_wrapper'
  | 'doctor_uuid'
  | 'receiver_name'
  | 'receiver_mobile'
  | 'receiver_address'
  | 'receiver_structure_address'
  | 'order_payment_amount'
  | 'transfer_amount_after_tax'
  | 'first_print_prescription_time'
  | 'pay_status'
  | 'category'
  | 'user_profit_amount'
  | 'payment_type'
  | 'platform_name'
  | 'express'
  | 'institution_name'
  | 'send_medicine_order_status'
  | 'send_loading'
  | 'promotion_discount_amount'
  | 'doctor_remarks'
  | 'medicine_take_method'
  | 'medicine_take_code'
  | 'is_exist_refund_order'
  | 'delivery_start_time'
  | 'delivery_end_time'
  | 'processing_methods'
>;

/**
 * 订单列表接口返回单项数据
 */
export interface IOrderItem {
  /** 药厂名称 */
  brand_name: string;
  /** 药材类型 */
  dosage_form: number;
  /** 药效当量 */
  equivalent: number;
  /** 药效当量的系数 */
  coefficient: number;
  /** 批发场景下的药效当量数值（= 药效当量 * 系数） */
  wholesale_equivalent: number;
  /** ?? */
  group: number;
  /** ?? */
  id: number;
  /** ?? */
  medicine: number;
  /** 药品单价 */
  medicine_price: number;
  /** 药品总价 */
  final_price: number;
  /** 药品uuid */
  medicine_sku: string;
  /** 药品名称 */
  name: string;
  /** 药品数量  */
  quantity: number;
  /** 零售单位 */
  retail_unit: string;
  /** 批发单位 */
  wholesale_unit: string;
  /** 药品简称 */
  short_name: string;
  /** 规格 */
  specification?: number | null;
  /** 服用单位 */
  take_unit: string;
  /** 特殊用法 */
  take_method_id: number | null;
  /** 药品类型 */
  type: EnumMedicineCategory;
  /** 厂家 */
  manufacturer: string;
  /** 药品类型 */
  west_category: string;
  /** 包装 */
  package: string;
  /** 西成药用法用量 */
  west_usage: string;
  /** 药库UUID */
  medicine_group_uuid: string;
  /** 药库名称 */
  medicine_group_name: string;
  /** 实发数量 */
  practical_quantity: number;
  /** 是否全部退款 */
  is_all_refund: boolean;
  /** 是否部分退款 */
  is_partial_refund: boolean;
  /** 药品适用的 sku 类型 零售单位 / 批发单位 */
  medicine_group_type: number; // 1-处方，2-药品，3-全部
  // 批次号
  batch_number: string;
  // 西/中成药的剂型
  west_dosage_form: string;
}

/**
 * 订单备注更新接口
 *
 * @export
 * @interface IOrderRemark
 */
export interface IOrderRemark {
  agent_remarks: string;
}

/**
 * 订单处方信息接口
 *
 * @export
 * @interface IOrderPrescription
 */
export interface IOrderPrescription {
  group: number;
  amount: number;
  /** 草药代煎每剂服用次数 */
  use_times: number;
  id: number;
  original_total_price: number;
  solve_prescription_id: number;
  processing_method: ProcessMethod;
  medicine_group_uuid: string;
  medicine_group_name: string;
  type: EnumMedicineCategory;
}

/** 方剂类型 */
export enum PrescriptionType {
  /**
   * 水冲服(颗粒)
   */
  Dissolve = 0,
  /**
   * 水煎服(草药)
   */
  Boil = 1,
  /**
   * 制丸(丸剂)
   */
  Pill = 2,
  /**
   * 制膏(膏方)
   */
  Paste = 3,
  /**
   * 制散(散剂)
   */
  Powder = 4,
  /**
   * @deprecated  -> 制丸
   * 小蜜丸
   */
  SmallPill = 5,
  /**
   * @deprecated  -> 制丸
   * 大蜜丸
   */
  BigPill = 6,
  /**
   * @deprecated  -> 制丸
   * 水丸
   */
  WaterPill = 7,
  /**
   * @deprecated -> 水煎服
   * 代煎
   */
  Delegate = 8,
}

export interface ISolvePrescriptions {
  prescription_primary: string;
  dose_ratio: number;
  medical_record: number;
  presc_items: IPrescItems[];
  id: number;
  prescription_type: number;
  prescription_amount: number;
  prescription_secondary: string;
  use_times: number;
  /** 默认药库 */
  default_medicine_group: string;
  /**
   * 默认药房类型 -1 - 脉景标准, 1 - 云药房, 2 - 我的药房
   */
  medicine_group_source: number;
  prescription_approach: string;
  prescription_group: string;
  taking_method: string;
  type: EnumMedicineCategory;
  processing_method: ProcessMethod;
}

export interface IPrescItems {
  take_method_id: number | null;
  pharmacy_equivalent: number;
  pharmacy_factory: string;
  pharmacy_unit: string;
  pharmacy_code: string;
  ts: number;
  pharmacy_agent: number;
  medicine_series: number;
  type: number;
  weight: number;
  medical_record: number;
  pharmacy_short: string;
  original_weight: number;
  pharmacy_quantity: number;
  tcm_name: string;
  tcm_uuid: string;
  sku: string;
}

/**
 * @description 电子处方快照
 * @export
 * @interface IPrescriptionSnapshot
 */
export interface IPrescriptionSnapshot {
  id: number;
  uuid: string;
  medical_record_code: string;
  medical_record_created_at: string;
  doctor_name: string;
  prescription_title: string;
  doctor_type: number;
  clinic_name: string;
  clinic_phone: string;
  clinic_address: string;
  patient_name: string;
  patient_phone: string;
  patient_address: string;
  patient_gender: string;
  patient_age: number;
  /** 用法 */
  taking_method: string;
  /** 病机 */
  syndrome_type: string | null;
  /** 疾病诊断 */
  general_diseases: RecordGeneralDiseaseEntity[];
  // 电子处方
  solve_prescriptions: ISolvePrescriptions[];
  cooperative_diagnosis?: {
    doctor_name: string; // '会诊医生，上级有处方权医生 or None',
    status: 'WAITING' | 'NOT_PASS' | 'HAVE_PASSED' | 'CANCELLED';
    is_approve_external: boolean; // 是否为外部审核
  };
}

/** 病历疾病 */
export interface RecordGeneralDiseaseEntity {
  /** 疾病UUID */
  uuid: string;
  /** 疾病名称 */
  name: string;
  /** 疾病CODE */
  code: string;
  /** 疾病类型 */
  type: EnumDiseaseType;
  /**
   * 是否是主要疾病
   */
  is_main?: boolean;
  /** 疾病属性 */
  attributes: RecordSymptomAttributeEntity[];
  /** 疾病的自定义属性 */
  remark: string;
  /**
   * @description timestamp 单位ms；
   * 一般用于用户输入先后的排序；
   * 由PC端生成，注意PC端时钟的不准确性。
   */
  ts: number;
  /** 自定义类型 */
  custom_type: SymptomType;
}

/** 症状/疾病属性 */
export interface RecordSymptomAttributeEntity {
  /** 属性uuid，包含同义词，和复合词 */
  attr_uuid: string;
  /** 属性名称 */
  attr_name: string;
  /**
   * @description timestamp 单位ms；
   * 一般用于用户输入先后的排序；
   * 由PC端生成，注意PC端时钟的不准确性。
   */
  ts: number;
}

/**
 * 支付方式接口
 *
 * @export
 * @interface IOrderPayment
 */
export interface IOrderPayment {
  /**
   * 多语言key
   */
  key: string;
  /**
   * 支付类型
   */
  value: ORDER_PAYMENT;
  /**
   * 是否可在设置页面设置
   */
  is_set: boolean;
  /**
   * 是否可以显示(处理合并显示)
   */
  is_show: boolean;
  /**
   * 中文描述
   */
  desc: string;
}

export interface IOrderStatus {
  /**
   * 多语言key
   */
  key: string;
  /**
   * 支付类型
   */
  value: ORDER_STATUS;
  /**
   * 中文描述
   */
  desc: string;
}

export interface ICurrencyType {
  /**
   * 多语言key
   */
  key: string;
  /**
   * 币种
   */
  value: string;
  /**
   * 中文描述
   */
  desc: string;
  /**
   * true仅显示国内，false仅显示国外，undefined同时显示
   */
  isDomestic: boolean | undefined;
}

/**
 * 订单物流信息
 *
 * @export
 * @interface ILogistics
 */
export interface ILogistics {
  /** 物流单号 */
  expressNumber: string;
  /** 物流公司 */
  logisticsCompany: string;
}

/** 订单列表查询参数 */
export interface IOrderSearchParam {
  ordering?: string;
  limit: number;
  offset: number;
}

/** 退款详情 */
export interface IOrderRefund {
  /** 业务订单号 */
  business_out_trade_no: string;
  /** 申请时间 */
  apply_time: string;
  /** 退款订单号 */
  out_trade_no: string;
  /** 退款方式 */
  type: 'FULL_REFUND' | 'PARTIAL_REFUND';
  /** 退款金额 */
  amount: number;
  /** 退款原因 */
  reason: string;
  /** 退款状态，100-退款审核，110-待退款，120-退款驳回，130-退款失败，140-退款成功 */
  status: number;
  /** 退款运费，全额退款时该字段为None */
  shipping_cost?: number;
  /** 驳回原因 */
  reject_reason?: string;
  /** 退货药品，全额退款时该字段为None	 */
  goods?: IOrderRefundGood[];
}

/** 退货药品 */
export interface IOrderRefundGood {
  /** uuid */
  uuid: string;
  /** name */
  name: string;
  /** 当量 */
  equivalent: number;
  /** 退款数量 */
  quantity: number;
  /** 购买单位 */
  purchase_unit: string;
  /** 当量单位 */
  equivalent_unit: string;
  /** 药品类型 */
  type: EnumMedicineCategory;
}

export enum EnumRefundStatus {
  退款审核 = 100,
  待退款 = 110,
  退款驳回 = 120,
  退款失败 = 130,
  退款成功 = 140,
}



// WEBPACK FOOTER //
// ./src/entity/OrderEntity.ts